import type { ComponentPropsWithRef, ReactElement } from 'react';
import { useId } from 'react';
import type { FieldError } from 'react-hook-form';
import InputGroup from '../InputGroup';
import { InputControl } from './private/InputControl';
import { InputLabel } from './private/InputLabel';
import { useInputHandlers } from './private/useInputHandlers';

export { BaseInput } from './private/BaseInput';

export type InputProps = {
  name: string;
  label?: string | React.ReactNode;
  labelClassName?: string;
  labelDescription?: string | React.ReactNode;
  inputClassName?: string;
  inputContainerClassName?: string;
  tooltip?: string;
  error?: FieldError | false;
  // allow for overriding the error message with a custom one (e.g. with extra context from other form fields etc.)
  errorMessage?: (error: FieldError) => string;
  valid?: boolean;
  after?: React.ReactNode;
  maxLength?: number;
  hideError?: boolean;
  // we have to hardcode defaultValue type instead picking it from <input>,
  // as native input element accepts string[], and <NumericFormat> from 'react-number-format does not
  defaultValue?: string | null;
} & ComponentPropsWithRef<'input'>;

const Input = ({
  label,
  labelClassName,
  labelDescription,
  className,
  tooltip,
  onKeyDown,
  onPaste,
  type = 'text',
  ...controlProps
}: InputProps): ReactElement => {
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const handlers = useInputHandlers({ type, onKeyDown, onPaste });

  return (
    <InputGroup className={className}>
      <InputLabel
        inputId={inputId}
        label={label}
        labelClassName={labelClassName}
        labelDescription={labelDescription}
        tooltip={tooltip}
      />
      <InputControl
        {...controlProps}
        defaultPlaceholder={typeof label === 'string' ? label : ''}
        errorId={errorId}
        handlers={handlers}
        id={inputId}
        type={type}
      />
    </InputGroup>
  );
};

export default Input;
